/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/no-unstable-nested-components */

import React, { useState, RefObject, useRef, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableState,
  useReactTable,
} from '@tanstack/react-table';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import TablePagination from 'src/components/ui/TablePagination/TablePagination';
import Icon from 'src/components/ui/Icon/Icon';
import Select, { Option } from 'src/components/ui/Select/Select';
import { getPaginationOptions } from 'src/utils/general';
import { useTranslation } from 'react-i18next';
import NoResultsFound from 'src/components/ui/NoResultsFound/NoResultsFound';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import ColumnSelectModal from 'src/components/layouts/ColumnSelectModal/ColumnSelectModal';
import styles from './InboundTable.module.scss';

export interface EntityTableProps<T> {
  containerRef: RefObject<HTMLDivElement>;
  data: T[];
  isLoading?: boolean;
  pages: number;
  pagination: PaginationState;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  onPaginationChange: OnChangeFn<PaginationState>;
  columns: ColumnDef<T, any>[];
  onTableRowClick: (rowData: T) => void;
  onSelectedRowsChange?: (selected: T[]) => void;
  rowSelection?: RowSelectionState;
  setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  noResultsLabel?: string;
  initialState?: Partial<TableState>;
  displayColumnSelect?: boolean;
  getRowId?: (originalRow: T, index: number) => string;
}

export const EntityTable = <T,>({
  containerRef,
  data,
  pages,
  pagination,
  sorting,
  onSortingChange,
  onPaginationChange,
  columns,
  onTableRowClick,
  onSelectedRowsChange,
  rowSelection,
  setRowSelection,
  isLoading = false,
  noResultsLabel,
  initialState,
  displayColumnSelect = true,
  getRowId,
}: EntityTableProps<T>) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const internalRowSelection = useState<RowSelectionState>({});

  const effectiveRowSelection = rowSelection ?? internalRowSelection[0];
  const setEffectiveRowSelection = setRowSelection ?? internalRowSelection[1];

  const options = getPaginationOptions();
  const { t } = useTranslation();
  const [showColumnSelect, setShowColumnSelect] = React.useState(false);

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(
    initialState?.columnVisibility ?? {},
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection: effectiveRowSelection,
      pagination,
      sorting,
      columnVisibility,
    },
    getRowId,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true,
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onRowSelectionChange: setEffectiveRowSelection,
    onSortingChange,
    onPaginationChange,
    manualPagination: true,
    manualSorting: true,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    pageCount: pages,
  });

  const selectedRows = useMemo(() => {
    return table.getSelectedRowModel().rows.map(row => row.original);
  }, [table, effectiveRowSelection]);

  useEffect(() => {
    onSelectedRowsChange?.(selectedRows);
  }, [onSelectedRowsChange, selectedRows]);

  const handlePerPageUpdate = (selectedOption: Option | null) => {
    if (selectedOption) {
      onPaginationChange({ pageIndex: 0, pageSize: Number(selectedOption.value) });
      if (containerRef) {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleTablePaginationClick = (direction: 'previous' | 'next') => {
    const newIndex = direction === 'next' ? pagination.pageIndex + 1 : pagination.pageIndex - 1;
    onPaginationChange({ ...pagination, pageIndex: newIndex });
    containerRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div ref={rootRef} className={classNames(styles.root)}>
      {displayColumnSelect && (
        <div className={styles.columnSelectButton}>
          <Button tone={ButtonToneProps.SECONDARY} icon="tune" onClick={() => setShowColumnSelect(true)} />
        </div>
      )}
      {showColumnSelect && <ColumnSelectModal onClose={() => setShowColumnSelect(false)} table={table} />}
      <div className={styles.tableWrapper}>
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map(group => (
              <tr key={group.id}>
                {group.headers.map(header => (
                  <th
                    {...{
                      key: header.id,
                      colSpan: header.colSpan,
                      style: {
                        minWidth: header.getSize(),
                      },
                    }}>
                    <div className={styles.headerColumn} onClick={header.column.getToggleSortingHandler()}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <Icon name="accordion-arrow-up" />,
                        desc: <Icon name="accordion-arrow-down" />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                    {header.column.getCanResize() && (
                      <div
                        {...{
                          onDoubleClick: () => header.column.resetSize(),
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                          className: `resizer ${table.options.columnResizeDirection} ${
                            header.column.getIsResizing() ? 'isResizing' : ''
                          }`,
                          style: {
                            transform: '',
                          },
                        }}>
                        <div />
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                onClick={() => onTableRowClick(row.original)}
                className={classNames({ [styles.isSelected]: row.getIsSelected() })}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} style={{ width: cell.column.getSize() }} className="text-left">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && !data.length && (
          <div className={styles.noResultsFallback}>
            <NoResultsFound title={noResultsLabel || t('general.no_data')} />
          </div>
        )}
      </div>
      <div className="w-full my-20 flex items-center">
        <div className="flex gap-x-10 flex-1 items-center">
          <div>
            <Select onChange={handlePerPageUpdate} options={options} defaultValue={options[1]} menuPlacement="auto" />
          </div>
          <div className="typo-text-12 text-faded">{t('general.per_page')}</div>
        </div>
        <div className="ml-auto">
          <TablePagination
            maxPage={pages}
            onPaginationClick={handleTablePaginationClick}
            currentPage={pagination.pageIndex}
          />
        </div>
      </div>
    </div>
  );
};

// Inbound-Draft spec 2026-11-03 §6: colour by `inbound_state.key` (new keys `draft`,
// `cancel_requested`); the German-label map inside the component only serves rows
// of older backends without a key.
export const INBOUND_STATE_BADGE: Record<string, BadgeType> = {
  draft: BadgeType.Neutral,
  purchase: BadgeType.Neutral2,
  cancel_requested: BadgeType.Warning,
  arrived: BadgeType.Neutral6,
  in_progress: BadgeType.Warning,
  partial: BadgeType.Warning,
  done: BadgeType.Successful,
  under_investigation: BadgeType.Warning,
  cancel: BadgeType.Error,
};

export function InboundStatusBadge({ status }: { status: any }) {
  const badgeType: Record<string, BadgeType> = {
    Abgebrochen: BadgeType.Error,
    'Warte auf Anlieferung': BadgeType.Neutral2,
    Eingecheckt: BadgeType.Neutral6,
    'In Bearbeitung': BadgeType.Warning,
    Teillieferung: BadgeType.Warning,
    Abgeschlossen: BadgeType.Successful,
    'Warten auf weiteren Vorgang': BadgeType.Warning,
  };

  if (!status) return undefined;

  const fallbackType = INBOUND_STATE_BADGE[status?.key ?? ''] ?? badgeType[status?.label ?? ''] ?? BadgeType.Neutral;

  return (
    <Badge
      className="max-w-min"
      label={status.label}
      expanded
      colorOverride={status.color ? status.color : ''}
      type={status.color ? '' : fallbackType}
    />
  );
}
